import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Icon from "../../layouts/PortalLayout/icons";
import type { AlertItem } from "../../helpers/api/admin-alerts";
import { isSoundOn, onNewAlerts, setSoundOn, useAdminAlerts } from "./useAdminAlerts";
import styles from "./AlertsBell.module.scss";

// =============================================================================
// The topbar bell: unread badge, a dropdown of the latest notifications, and a
// toast + sound when a new one arrives. Clicking a notification marks it read and
// opens the item it is about.
// =============================================================================

const ago = (iso: string): string => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? "yesterday" : `${d} days ago`;
};

const AlertsBell = () => {
  const { items, unread, markRead, markAllRead } = useAdminAlerts();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(isSoundOn());
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  const openItem = (item: AlertItem) => {
    setOpen(false);
    if (!item.readAt) void markRead(item.id);
    if (item.path) navigate(item.path);
  };

  // New arrivals: one toast each (at most three, then a summary).
  useEffect(
    () =>
      onNewAlerts((fresh) => {
        fresh.slice(0, 3).forEach((item) =>
          toast.info(
            <div>
              <strong>{item.title}</strong>
              {item.description ? <div className={styles.toastText}>{item.description}</div> : null}
            </div>,
            { onClick: () => openItem(item), autoClose: 8000 },
          ),
        );
        if (fresh.length > 3) toast.info(`${fresh.length - 3} more new notifications`, { onClick: () => setOpen(true) });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleSound = () => {
    setSoundOn(!sound);
    setSound(!sound);
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.bell}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        data-testid="alerts-bell"
      >
        <span className={styles.bellIcon}>
          <Icon name="bell" size={16} />
          {unread ? <span className={styles.badge}>{unread > 9 ? "9+" : unread}</span> : null}
        </span>
        <span className={styles.bellLabel}>Notifications</span>
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Notifications</span>
            {unread > 0 && (
              <button type="button" className={styles.linkBtn} onClick={() => void markAllRead()}>
                Mark all as read
              </button>
            )}
          </div>

          <ul className={styles.list}>
            {items.length === 0 && <li className={styles.empty}>Nothing yet. New supplier activity will show up here.</li>}
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`${styles.item}${item.readAt ? "" : ` ${styles.unread}`}`}
                  onClick={() => openItem(item)}
                >
                  <span className={styles.dot} aria-hidden="true" />
                  <span className={styles.itemBody}>
                    <span className={styles.itemTitle}>{item.title}</span>
                    {item.description ? <span className={styles.itemText}>{item.description}</span> : null}
                    <span className={styles.itemTime}>{ago(item.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className={styles.panelFoot}>
            <Link to="/account/notifications" className={styles.linkBtn} onClick={() => setOpen(false)}>
              Choose notifications
            </Link>
            <button type="button" className={styles.linkBtn} onClick={toggleSound} aria-pressed={sound}>
              Sound: {sound ? "on" : "off"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsBell;
